import { ProductSetup } from '@futrli-sdk/organisations';
import { ActivateProductSidebar } from '@futrli/billing';
import { FormattedMessage } from '@futrli/localisation';
import { isInTrialPeriod, ProductSlug, ROUTES } from '@futrli/shared-services';
import classNames from 'classnames';
import { differenceInDays, differenceInHours } from 'date-fns';
import { capitalize } from 'lodash';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import styles from './notifications.scss';
import { trackCTA, trackEvent } from '../core/tracker';
import { TRACKING_EVENTS } from '../core/constants';
import { FlashItem } from './flash-item';

export enum TrialPeriodTimeUnits {
  hours = 'hours',
  days = 'days'
}
interface Props {
  productSlug: ProductSlug;
  productSetup: ProductSetup;
  trialPeriodTimeInHours: number;
  formatTrialPeriodTimeUnitsTo: keyof typeof TrialPeriodTimeUnits;
}
const DAY_HOURS = 24;

/**
 * Functional component to display the "go pro" banner.
 *
 * @returns {UIFlashItem | null}
 */
export const UpgradeToProBanner: React.FC<Props> = ({
  productSlug,
  productSetup,
  trialPeriodTimeInHours,
  formatTrialPeriodTimeUnitsTo
}: Props) => {
  const { pathname } = useLocation();
  const { workspaceId, organisationId } = useParams();
  const [isInTrial, setIsInTrial] = React.useState<boolean | null>(null);

  const handleSuccess = () => {
    onActivate();
  };

  React.useEffect(() => {
    setIsInTrial(isInTrialPeriod(productSetup, trialPeriodTimeInHours));
  }, [productSlug, productSetup, trialPeriodTimeInHours]);

  if (isInTrial === null || pathname.includes(ROUTES.ONBOARDING_ROUTE)) {
    return null;
  }

  const item = {
    id: 'upgrade-to-pro',
    title: (
      <span className={classNames(styles.title, 'uiTypeNotificationBody')}>
        {isInTrial ? (
          <React.Fragment>
            <FormattedMessage
              id="notification.you_have_period_left_subscribe"
              defaultMessage="You have {TRIAL_PERIOD} of your trial left. Subscribe now"
              values={{
                TRIAL_PERIOD: getTrialPeriodTimeMessage(
                  trialPeriodTimeInHours,
                  formatTrialPeriodTimeUnitsTo,
                  productSetup
                )
              }}
            />
            <ActivateProductSidebar
              workspaceId={workspaceId}
              organisationId={organisationId}
              productSlug={productSlug}
              wrapperComponent="button"
              onSuccess={handleSuccess}
              onClickCTA={onClickCTA}
            >
              <span data-testid="upgrade-cta">
                <FormattedMessage id="common.here_dot" defaultMessage="here." />
              </span>
            </ActivateProductSidebar>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <FormattedMessage
              id="notification.you_are_using_product_for_free"
              defaultMessage={`
                You’re using {PRODUCT} for free. To dive deeper into your
                insights and gain access to our full range of features,
              `}
              values={{
                PRODUCT: capitalize(productSlug)
              }}
            />
            <ActivateProductSidebar
              workspaceId={workspaceId}
              organisationId={organisationId}
              productSlug={productSlug}
              wrapperComponent="button"
              onSuccess={handleSuccess}
              onClickCTA={onClickCTA}
            >
              <span>
                <FormattedMessage id="notification.upgrade_to_pro_now" defaultMessage="upgrade to Pro now!" />
              </span>
            </ActivateProductSidebar>
          </React.Fragment>
        )}
      </span>
    )
  };

  return <FlashItem item={item} />;
};

/**
 * Called on product being activated.
 */
export const onActivate = () => {
  window.location.reload();
};

/**
 * Called when clicking "go pro" call to action.
 *
 * @param {React.MouseEvent} event
 */
export const onClickCTA = (event: React.MouseEvent<HTMLElement>) => {
  event.preventDefault();
  trackEvent(TRACKING_EVENTS.VIEWED_GO_PRO_MODAL);
  trackCTA('Banner upgrade', event.currentTarget.innerText);
};

/**
 * Gets the trial period time message component.
 * @param {number} trialPeriodTimeInHours
 * @param {ProductSetup} productSetup
 * @param {TrialPeriodTimeUnits} formatTrialPeriodTimeUnitsTo
 * @returns {JSX.Element}
 */
export const getTrialPeriodTimeMessage = (
  trialPeriodTimeInHours: number,
  formatTrialPeriodTimeUnitsTo: keyof typeof TrialPeriodTimeUnits,
  productSetup: ProductSetup
): JSX.Element => {
  if (formatTrialPeriodTimeUnitsTo === TrialPeriodTimeUnits.days) {
    return (
      <FormattedMessage
        id="notification.value_days"
        values={{
          value: getLeftTrialPeriodTime(trialPeriodTimeInHours, productSetup, formatTrialPeriodTimeUnitsTo)
        }}
        defaultMessage={`{value, plural, one {1 day} other {{value} days}}`}
      />
    );
  }

  return (
    <FormattedMessage
      id="notification.value_hours"
      values={{
        value: getLeftTrialPeriodTime(trialPeriodTimeInHours, productSetup, formatTrialPeriodTimeUnitsTo)
      }}
      defaultMessage={`{value, plural, one {1 hour} other {{value} hours}}`}
    />
  );
};

/**
 * Gets the remaining time (hours/days) of the trial if it is not expired, returns zero instead.
 * Days case: If the trial hours for some reason are not multiple of a day (24h) and the result is
 * for example 1,6666666 days, then we round up to the next high integer "2 days".
 *
 * @param {Number} trialTimeHours
 * @param {ProductSetup} productSetup
 * @param {TrialPeriodTimeUnits} formatTrialPeriodTimeUnitsTo
 */
export const getLeftTrialPeriodTime = (
  trialTimeHours: number,
  productSetup: ProductSetup,
  formatTrialPeriodTimeUnitsTo: keyof typeof TrialPeriodTimeUnits
): number => {
  const DEFAULT_TIME_LEFT = 0;
  const createdDate = new Date(productSetup.created);

  if (formatTrialPeriodTimeUnitsTo === TrialPeriodTimeUnits.hours) {
    const hoursLeft = trialTimeHours - differenceInHours(Date.now(), createdDate);

    return hoursLeft < 0 ? DEFAULT_TIME_LEFT : hoursLeft;
  }

  const diffDays = differenceInDays(Date.now(), createdDate);
  const daysLeft = trialTimeHours / DAY_HOURS - diffDays;

  return daysLeft < 0 ? DEFAULT_TIME_LEFT : Math.ceil(daysLeft);
};
